import React, { useMemo, useState, useEffect, useRef } from 'react'
import { Table, Checkbox, message } from 'antd'
import type { TableProps } from 'antd'
import styles from '../Organizations.module.less'
import {
  ProductClaim,
  PermissionNode,
  PermissionValue,
  actionSuffixs,
} from '~/Services/RoleServices'
import { useAppSelector, useAppDispatch } from '~/Store/hooks'
import { useTranslation } from 'react-i18next'
import { OrganizationRoleActions } from '~/Store/OrganizationRoles'
import { getDefaultOrganization } from '~/Store/User/selectors'
import { Utils } from '~/Utils'

const TABLE_HEIGHT_OFFSET = 350

export function convertClaimsToPermissionTree(
  claims: ProductClaim[],
): PermissionNode[] {
  const nodeMap = new Map<string, PermissionNode>()

  const getNode = (key: string): PermissionNode => {
    if (!nodeMap.has(key)) {
      const parts = key.split('.')

      nodeMap.set(key, {
        id: key,
        key: key,
        name: key,
        displayName: parts[parts.length - 1],
        parentId: null,
        parentName: null,
        permissions: {},
      })
    }

    return nodeMap.get(key)!
  }

  claims.forEach(claim => {
    const parts = claim.claimValue.split('.')
    const last = parts[parts.length - 1]

    const isAction = actionSuffixs.indexOf(last) !== -1

    if (isAction) {
      const nodePath = claim.parentClaimValue ?? parts.slice(0, -1).join('.')
      const node = getNode(nodePath)

      node.permissions[last] = claim.isGranted ?? false
    } else {
      getNode(claim.claimValue)
    }
  })

  claims.forEach(claim => {
    const parts = claim.claimValue.split('.')
    const last = parts[parts.length - 1]

    const isAction = actionSuffixs.indexOf(last) !== -1
    if (isAction) return

    const node = getNode(claim.claimValue)

    let parentKey = claim.parentClaimValue

    if (!parentKey && parts.length > 1) {
      parentKey = parts.slice(0, -1).join('.')
    }

    if (!parentKey) return

    const parent = getNode(parentKey)

    node.parentId = parent.id
    node.parentName = parent.name

    parent.children ??= []

    if (!parent.children.find(c => c.id === node.id)) {
      parent.children.push(node)
    }
  })

  nodeMap.forEach(node => {
    actionSuffixs.forEach(action => {
      if (!(action in node.permissions)) {
        node.permissions[action] = 'NOT_EXIST'
      }
    })

    if (!node.children?.length) delete node.children
  })

  const roots: PermissionNode[] = []

  nodeMap.forEach(node => {
    if (!node.parentId) roots.push(node)
  })

  return roots
}

const permissionGroups = [
  {
    name: 'View',
    permissions: ['Read', 'Volume', 'Value'],
  },
  {
    name: 'Operation',
    permissions: [
      'Create',
      'Update',
      'Delete',
      'Print',
      'Import',
      'Export',
      'Confirm',
    ],
  },
  {
    name: 'Administration',
    permissions: ['Manage'],
  },
]

export const flattenTree = (nodes: PermissionNode[]): PermissionNode[] => {
  const result: PermissionNode[] = []

  const walk = (items: PermissionNode[]) => {
    items.forEach(i => {
      result.push(i)
      if (i.children) walk(i.children)
    })
  }

  walk(nodes)

  return result
}

interface PermissionGroupsTableProps {
  searchKeyword?: string
}

export const PermissionGroupsTable = ({
  searchKeyword = '',
}: PermissionGroupsTableProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const token = useAppSelector(state => state.user.token)
  const clientId = Utils.getClientIdFromToken(token)

  const productClaims = useAppSelector(
    state => state.organizationRoles.currentRoleClaims,
  )

  const claimsLoading = useAppSelector(
    state => state.organizationRoles.claimsLoading,
  )

  const selectedRole = useAppSelector(state => state.organization.roleSelected)
  const defaultOrg = useAppSelector(getDefaultOrganization)

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])
  const [permissionState, setPermissionState] = useState<
    Record<string, Record<string, PermissionValue>>
  >({})

  const originalPermissionState = useRef<
    Record<string, Record<string, PermissionValue>>
  >({})

  const allTreeNodes = useMemo(() => {
    if (!productClaims) return []
    return convertClaimsToPermissionTree(productClaims)
  }, [productClaims])

  const dataSource = useMemo(() => {
    if (!searchKeyword.trim()) return allTreeNodes

    const keyword = searchKeyword.toLowerCase().trim()
    const matchedNodeIds = new Set<string>()

    const checkNode = (node: PermissionNode): boolean => {
      const translated = t(node.name)
      const displayText =
        translated !== node.name ? translated : node.displayName
      const nameMatch = displayText.toLowerCase().includes(keyword)

      if (nameMatch) {
        matchedNodeIds.add(node.id)
        const addAllChildren = (n: PermissionNode) => {
          matchedNodeIds.add(n.id)
          n.children?.forEach(addAllChildren)
        }
        node.children?.forEach(addAllChildren)
        return true
      }

      const childMatches = node.children?.some(checkNode) ?? false
      if (childMatches) {
        matchedNodeIds.add(node.id)
        return true
      }

      return false
    }

    allTreeNodes.forEach(checkNode)

    const filterTree = (nodes: PermissionNode[]): PermissionNode[] => {
      return nodes
        .filter(node => matchedNodeIds.has(node.id))
        .map(node => ({
          ...node,
          children: node.children ? filterTree(node.children) : undefined,
        }))
    }

    return filterTree(allTreeNodes)
  }, [allTreeNodes, searchKeyword, t])

  const allNodes = useMemo(() => flattenTree(allTreeNodes), [allTreeNodes])

  const calculateParentState = (
    node: PermissionNode,
    action: string,
    state: Record<string, Record<string, PermissionValue>>,
  ): PermissionValue => {
    if (!node.children?.length) {
      return state[node.id][action]
    }

    const values = node.children
      .map(c => calculateParentState(c, action, state))
      .filter(v => v !== 'NOT_EXIST')

    if (!values.length) return state[node.id][action]

    const allTrue = values.every(v => v === true)
    const allFalse = values.every(v => v === false)

    if (allTrue) return true
    if (allFalse) return false
    return 'PARTIAL'
  }

  useEffect(() => {
    const map: Record<string, Record<string, PermissionValue>> = {}

    allNodes.forEach(node => {
      map[node.id] = { ...node.permissions }
    })

    allNodes.forEach(node => {
      if (node.children?.length) {
        actionSuffixs.forEach(action => {
          const calculatedValue = calculateParentState(node, action, map)
          map[node.id][action] = calculatedValue
        })
      }
    })

    setPermissionState(map)
    originalPermissionState.current = structuredClone(map)
  }, [allNodes, allTreeNodes])

  useEffect(() => {
    const nodesToExpand = searchKeyword.trim()
      ? flattenTree(dataSource)
      : allNodes
    setExpandedRowKeys(nodesToExpand.map(i => i.id))
  }, [dataSource, searchKeyword, allNodes])

  const getChildren = (node: PermissionNode): PermissionNode[] => {
    const result: PermissionNode[] = []

    const walk = (n: PermissionNode) => {
      if (n.children) {
        n.children.forEach(c => {
          result.push(c)
          walk(c)
        })
      }
    }

    walk(node)
    return result
  }

  const getParent = (node: PermissionNode) => {
    return allNodes.find(n => n.id === node.parentId)
  }

  const updateParents = (
    node: PermissionNode,
    action: string,
    newState: Record<string, Record<string, PermissionValue>>,
  ) => {
    let parent = getParent(node)

    while (parent) {
      const children = parent.children ?? []

      const values = children
        .map(c => newState[c.id]?.[action])
        .filter(v => v !== 'NOT_EXIST')

      if (!values.length) break

      const allTrue = values.every(v => v === true)
      const allFalse = values.every(v => v === false)

      if (allTrue) newState[parent.id][action] = true
      else if (allFalse) newState[parent.id][action] = false
      else newState[parent.id][action] = 'PARTIAL'

      parent = getParent(parent)
    }
  }

  const getChangedClaims = (
    newState: Record<string, Record<string, PermissionValue>>,
  ): ProductClaim[] => {
    const changedClaims: ProductClaim[] = []

    const claimMap = new Map(
      productClaims?.map(claim => [claim.claimValue, claim]) ?? [],
    )

    const hasAnyGrantedForAction = (
      node: PermissionNode,
      action: string,
      state: Record<string, Record<string, PermissionValue>>,
    ): boolean => {
      const value = state[node.id]?.[action]

      if (value === true) return true

      return (
        node.children?.some(child =>
          hasAnyGrantedForAction(child, action, state),
        ) ?? false
      )
    }

    const hasAnyGranted = (
      node: PermissionNode,
      state: Record<string, Record<string, PermissionValue>>,
    ): boolean => {
      return actionSuffixs.some(action =>
        hasAnyGrantedForAction(node, action, state),
      )
    }

    allNodes.forEach(node => {
      actionSuffixs.forEach(action => {
        const originalValue = originalPermissionState.current[node.id]?.[action]
        const newValue = newState[node.id]?.[action]

        if (
          originalValue !== newValue &&
          (newValue === true || newValue === false)
        ) {
          const claimValue = `${node.name}.${action}`

          const existingClaim = claimMap.get(claimValue)

          if (existingClaim) {
            changedClaims.push({
              claimType: existingClaim.claimType,
              claimValue,
              isGranted: newValue,
              parentClaimValue: existingClaim.parentClaimValue,
            })
          }
        }
      })

      const originalGranted = hasAnyGranted(
        node,
        originalPermissionState.current,
      )
      const newGranted = hasAnyGranted(node, newState)

      if (originalGranted !== newGranted) {
        const claimValue = node.name

        const existingClaim = claimMap.get(claimValue)

        if (existingClaim) {
          changedClaims.push({
            claimType: existingClaim.claimType,
            claimValue,
            isGranted: newGranted,
            parentClaimValue: existingClaim.parentClaimValue,
          })
        }
      }
    })

    return changedClaims
  }

  const togglePermission = (
    node: PermissionNode,
    action: string,
    checked: boolean,
  ) => {
    const previousState = structuredClone(permissionState)
    const newState = structuredClone(permissionState)

    newState[node.id][action] = checked

    const children = getChildren(node)

    children.forEach(c => {
      if (newState[c.id][action] !== 'NOT_EXIST' || c.children?.length) {
        newState[c.id][action] = checked
      }
    })

    updateParents(node, action, newState)
    setPermissionState(newState)

    if (!selectedRole?.id || !defaultOrg?.guid) return

    const changedClaims = getChangedClaims(newState)

    if (changedClaims.length > 0) {
      dispatch(
        OrganizationRoleActions.updateProductClaims({
          clientId,
          roleId: selectedRole.id,
          productClaims: changedClaims,
          onSuccess: () => {
            originalPermissionState.current = structuredClone(newState)
            message.success(t('updatedSuccessfully'))
          },
          onFailure: () => {
            setPermissionState(previousState)
            message.error(t('updateFailed'))
          },
        }),
      )
    }
  }

  const permissionVisibility = useMemo(() => {
    const map: Record<string, Record<string, boolean>> = {}

    const check = (node: PermissionNode, action: string): boolean => {
      const value = node.permissions?.[action]

      if (value !== 'NOT_EXIST') return true

      return node.children?.some(c => check(c, action)) ?? false
    }

    allNodes.forEach(node => {
      map[node.id] = {}

      actionSuffixs.forEach(action => {
        map[node.id][action] = check(node, action)
      })
    })

    return map
  }, [allNodes])

  const columns: TableProps<PermissionNode>['columns'] = useMemo(() => {
    const cols: TableProps<PermissionNode>['columns'] = [
      {
        title: t('Module'),
        dataIndex: 'displayName',
        key: 'displayName',
        fixed: 'left',
        render: (value: string, record: PermissionNode) => {
          const translated = t(record.name)
          const text = translated !== record.name ? translated : value
          const isParent = record.children?.length

          return (
            <span style={{ fontWeight: isParent ? 600 : 400 }}>{text}</span>
          )
        },
      },
    ]

    permissionGroups.forEach(group => {
      cols.push({
        title: t(group.name),
        key: group.name,
        align: 'center',
        children: group.permissions.map(action => ({
          title: t(action),
          key: action,
          width: 110,
          align: 'center',
          render: (_, record: PermissionNode) => {
            if (!permissionVisibility[record.id]?.[action]) return null

            const value = permissionState?.[record.id]?.[action]

            return (
              <Checkbox
                checked={value === true}
                indeterminate={value === 'PARTIAL'}
                onChange={e =>
                  togglePermission(record, action, e.target.checked)
                }
              />
            )
          },
        })),
      })
    })

    return cols
  }, [permissionState, permissionVisibility, togglePermission, t])

  const tableHeight = window.innerHeight - TABLE_HEIGHT_OFFSET

  return (
    <Table
      className={styles.PermissionTable}
      bordered
      size="small"
      rowKey="id"
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      loading={claimsLoading}
      expandable={{
        expandedRowKeys,
        onExpandedRowsChange: keys => setExpandedRowKeys([...keys]),
      }}
      scroll={{ x: 1600, y: tableHeight }}
    />
  )
}
